import React, { useCallback, useEffect, useState } from "react";
import GenericTable from "components/table";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { User } from "auth/AuthProvider";
import { Trash, User as UserIcon, X, Check } from "lucide-react";
import { Dialog } from "components/dialog";
import { useConfirmation } from "components/dialog/confirmation";
import api, { fetchUsersCurrentWorkspaceAPI } from "api/axios";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { useRoleInfo } from "layouts/role-info/_context/RoleinfoContext";
import { Image } from "components/Image";
import TooltipHorizon from "components/tooltip";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<User>();

function SearchTableUsersOverview(props: { tableData: User[] }) {
  const { t } = useTranslation();
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const { roleInfo, fetchRoleDetails } = useRoleInfo();

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [userSearchTerm, setUserSearchTerm] = React.useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = React.useState(false);
  const { confirm } = useConfirmation();

  // All users for multi-select
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersSkip, setUsersSkip] = useState(0);
  const USERS_TAKE = 20;

  // Fetch all users for multi-select
  const fetchUsersList = useCallback(
    async (reset = false) => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const response = await fetchUsersCurrentWorkspaceAPI({
          take: USERS_TAKE,
          skip: reset ? 0 : usersSkip,
          handler: {},
        });
        setUsersTotal(response.total);
        setUsers((prev) => (reset ? response.items : [...prev, ...response.items]));
        if (reset) setUsersSkip(USERS_TAKE);
        else setUsersSkip((prev) => prev + USERS_TAKE);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setUsersLoading(false);
      }
    },
    [usersSkip]
  );

  useEffect(() => {
    if (isAddDialogOpen) fetchUsersList(true);
  }, [isAddDialogOpen, fetchUsersList]);

  // Handler for deleting a row
  const handleDeleteRow = async (id: string) => {
    const confirmed = await confirm({
      title: t("roles.deleteUser"),
      message: t("roles.deleteUserMessage"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });
    if (confirmed) {
      setDeletingRowId(id);

      try {
        // TODO: temporary for backward compatibility
        await api.delete(`/groups/${roleInfo?.id}/users`, {
          data: { user_ids: [id] },
        });
        fetchRoleDetails();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setDeletingRowId(null);
      }
    }
  };

  const handleAddUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserIds.length === 0) return;

    setIsSubmitting(true);
    try {
      // TODO: temporary for backward compatibility
      await api.post(`/groups/${roleInfo?.id}/users`, {
        user_ids: selectedUserIds,
      });

      setIsAddDialogOpen(false);
      setSelectedUserIds([]);
      setIsUserDropdownOpen(false);
      setUserSearchTerm("");

      fetchRoleDetails();
    } catch (err) {
      toast.error(t("roles.failedToAddUsersToRole"));
      handleAxiosAndCaughtError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Multi-select logic
  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const removeUser = (userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const selectedUsers = users.filter((user) =>
    selectedUserIds.includes(user.id)
  );

  const columns: ColumnDef<User, any>[] = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => <p>{t("common.name")}</p>,
      cell: (info) => (
        <div className="flex items-center gap-3">
          <Image
            src={info.row.original.image}
            alt={info.getValue()}
            className="h-8 w-8 rounded-full object-cover"
          />
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <p>{t("common.email")}</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("id", {
      id: "actions",
      header: () => (
        <p className="p-4 text-left font-medium text-gray-900 dark:text-gray-100"></p>
      ),
      cell: (info) => (
        <TooltipHorizon
          extra=""
          placement="top"
          trigger={
            <button
              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
              onClick={() => {
                handleDeleteRow(info.getValue());
              }}
              disabled={deletingRowId?.trim().length > 0}
            >
              <Trash size={15} className="text-red-500" />
            </button>
          }
          content={<span>{t("roles.removeUser")}</span>}
        />
      ),
      meta: {
        cellClassName: "min-w-[50px]",
        headerClassName: "min-w-[50px]",
      },
    }),
  ];

  const dataWithActions = props.tableData.map((row) => ({
    ...row,
    deletingRowId,
    handleDeleteRow,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  return (
    <>
      <GenericTable<User>
        data={dataWithActions}
        columns={columns}
        renderRowClassName={renderRowClassName}
        searchPlaceholder={t("users.searchUsers")}
        onAdd={() => setIsAddDialogOpen(true)}
        debug={true}
        addLabel={t("roles.addUsersToRole")}
      />

      <Dialog isOpen={isAddDialogOpen} setIsOpen={setIsAddDialogOpen}>
        <form
          onSubmit={handleAddUsers}
          className="flex min-h-[70vh] w-full min-w-[280px] max-w-[500px] flex-col gap-4 sm:min-w-[400px] sm:gap-6"
        >
          <div className="border-b border-gray-200 pb-3 sm:pb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("roles.addUsersToRoleTitle")}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              {t("roles.selectUsersToAddToRole")}
            </p>
          </div>

          {/* Selected Users Display */}
          {selectedUsers.length > 0 && (
            <div className="mb-3 rounded-lg bg-gray-50 p-2.5 dark:bg-gray-800 sm:mb-4 sm:p-3">
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {selectedUsers.slice(0, 3).map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-1.5 rounded-full bg-brand-100 px-2.5 py-1 text-xs font-medium text-brand-800 dark:bg-brand-900 dark:text-brand-200 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm"
                  >
                    <Image
                      src={user.image}
                      alt={user.name}
                      className="h-4 w-4 rounded-full object-cover sm:h-5 sm:w-5"
                    />
                    <span className="truncate">{user.name}</span>
                    <button
                      type="button"
                      onClick={() => removeUser(user.id)}
                      className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-brand-200 dark:hover:bg-brand-800 sm:ml-1"
                    >
                      <X size={10} className="sm:h-3 sm:w-3" />
                    </button>
                  </div>
                ))}
                {selectedUsers.length > 3 && (
                  <div className="flex items-center gap-1.5 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm">
                    <span>{t("roles.roleInfo.users.moreUsers", { count: selectedUsers.length - 3 })}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const remainingUsers = selectedUsers
                          .slice(3)
                          .map((u) => u.name)
                          .join(", ");
                        toast.error(t("roles.roleInfo.users.otherSelectedUsers", { users: remainingUsers }));
                      }}
                      className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600 sm:ml-1"
                      title={t("roles.roleInfo.users.viewAllSelectedUsers")}
                    >
                      <UserIcon size={10} className="sm:h-3 sm:w-3" />
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 sm:mt-2">
                {t("roles.roleInfo.users.selectedUsersCount", { count: selectedUsers.length })}
              </p>
            </div>
          )}

          {/* User Search and Selection */}
          <div className="relative">
            <div className="relative">
              <UserIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 sm:left-3 sm:h-4 sm:w-4" />
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm placeholder-gray-400 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500 sm:py-2.5 sm:pl-10 sm:pr-4"
                placeholder={t("users.searchUsersByNameOrEmail")}
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                onFocus={() => setIsUserDropdownOpen(true)}
              />
            </div>
            {/* User Dropdown */}
            {isUserDropdownOpen && (
              <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800 sm:max-h-60">
                {usersLoading && users.length === 0 && (
                  <div className="flex items-center justify-center p-3 text-gray-500 sm:p-4">
                    {t("common.loading")}
                  </div>
                )}
                {usersError && users.length === 0 && (
                  <div className="flex flex-col items-center p-3 sm:p-4">
                    <span className="text-sm text-red-500">{usersError}</span>
                    <button
                      type="button"
                      className="mt-1.5 text-sm text-blue-500 underline sm:mt-2"
                      onClick={() => fetchUsersList(true)}
                    >
                      {t("common.retry")}
                    </button>
                  </div>
                )}
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleUserToggle(user.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 sm:gap-3 sm:px-4 sm:py-3 ${
                          isSelected ? "dark:bg-brand-900/50 bg-brand-50" : ""
                        }`}
                      >
                        <Image
                          src={user.image}
                          alt={user.name}
                          className="h-6 w-6 rounded-full object-cover sm:h-8 sm:w-8"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-gray-900 dark:text-white sm:text-sm">
                            {user.name}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                            {user.email}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400 sm:h-4 sm:w-4" />
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 sm:px-4 sm:py-3 sm:text-sm">
                    {t("users.noUsersFound")}
                  </div>
                )}
                {usersError && users.length > 0 && (
                  <div className="flex flex-col items-center p-1.5 sm:p-2">
                    <span className="text-xs text-red-500">{usersError}</span>
                    <button
                      type="button"
                      className="mt-1 text-xs text-blue-500 underline"
                      onClick={() => fetchUsersList(false)}
                    >
                      {t("common.retry")}
                    </button>
                  </div>
                )}
                {!usersLoading && users.length < usersTotal && !usersError && (
                  <button
                    type="button"
                    className="w-full p-1.5 text-center text-xs text-blue-500 hover:underline sm:p-2 sm:text-sm"
                    onClick={() => fetchUsersList(false)}
                  >
                    {t("roles.loadMore")}
                  </button>
                )}
                {usersLoading && users.length > 0 && (
                  <div className="flex items-center justify-center p-1.5 text-xs text-gray-400 sm:p-2">
                    {t("roles.loadingMore")}
                  </div>
                )}
              </div>
            )}
            {/* Click outside to close dropdown */}
            {isUserDropdownOpen && (
              <div
                className="fixed inset-0 z-0"
                onClick={() => setIsUserDropdownOpen(false)}
              />
            )}
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <button
              type="button"
              onClick={() => {
                setIsAddDialogOpen(false);
                setSelectedUserIds([]);
                setIsUserDropdownOpen(false);
                setUserSearchTerm("");
              }}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:px-4"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-brand-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting || selectedUserIds.length === 0}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                  {t("roles.adding")}
                </div>
              ) : (
                t("roles.addUsers")
              )}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

export default SearchTableUsersOverview;
